import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useMutation } from '@tanstack/react-query';
import { AlertCircle, Loader2 } from 'lucide-react';
import { getBlockchainAPIAdapter } from '@/lib/blockchain-api-adapter';
import { DetailedTransaction } from '@shared/schema';

export default function BlockdaemonTestPage() {
  const [activeTab, setActiveTab] = useState('address');
  const [addressInput, setAddressInput] = useState('1A1zP1eP5QGefi2DMPTfTL5SLmv7DivfNa');
  const [txidInput, setTxidInput] = useState('bf87984f536d412070976fe36d853db7e8100627d0fc9fc83ce895f35c9858d4');
  const [queryInput, setQueryInput] = useState('');
  const [addressResult, setAddressResult] = useState<any>(null);
  const [txResult, setTxResult] = useState<DetailedTransaction | null>(null);
  const [searchResult, setSearchResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const blockchainAPI = getBlockchainAPIAdapter();

  // Address query mutation
  const addressMutation = useMutation({
    mutationFn: async () => {
      setError(null);
      try {
        console.log(`Fetching address data for ${addressInput}`);
        const data = await blockchainAPI.getAddressData(
          addressInput,
          1, // page
          10, // limit
          null, // startDate
          null // endDate
        );
        setAddressResult(data);
        return data;
      } catch (error) {
        console.error("Error fetching address data:", error);
        setError(error instanceof Error ? error.message : "Failed to fetch address data");
        throw error;
      }
    }
  });

  // Transaction query mutation
  const txMutation = useMutation({
    mutationFn: async () => {
      setError(null);
      try {
        console.log(`Fetching transaction data for ${txidInput}`);
        const data = await blockchainAPI.getTransactionDetails(txidInput);
        setTxResult(data);
        return data;
      } catch (error) {
        console.error("Error fetching transaction data:", error);
        setError(error instanceof Error ? error.message : "Failed to fetch transaction data");
        throw error;
      }
    }
  });

  // Search mutation
  const searchMutation = useMutation({
    mutationFn: async () => {
      setError(null);
      try {
        console.log(`Searching for ${queryInput}`);
        const data = await blockchainAPI.searchBlockchain(queryInput);
        setSearchResult(data);
        return data;
      } catch (error) {
        console.error("Error searching blockchain:", error);
        setError(error instanceof Error ? error.message : "Search failed");
        throw error;
      }
    }
  });

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    setError(null);
  };

  return (
    <div className="container py-10">
      <h1 className="text-3xl font-bold mb-6">Blockdaemon API Test</h1>
      
      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      
      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="address">Address</TabsTrigger>
          <TabsTrigger value="transaction">Transaction</TabsTrigger>
          <TabsTrigger value="search">Search</TabsTrigger>
        </TabsList>
        
        <TabsContent value="address">
          <Card>
            <CardHeader>
              <CardTitle>Address Data Test</CardTitle>
              <CardDescription>
                Enter a Bitcoin address to retrieve its details and transactions.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex space-x-2">
                <Input
                  value={addressInput}
                  onChange={(e) => setAddressInput(e.target.value)}
                  placeholder="Bitcoin address"
                  className="flex-grow"
                />
                <Button 
                  onClick={() => addressMutation.mutate()}
                  disabled={addressMutation.isPending}
                >
                  {addressMutation.isPending ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading</>
                  ) : (
                    'Fetch Data'
                  )}
                </Button>
              </div>

              {addressResult && (
                <div className="mt-4 max-h-[500px] overflow-auto">
                  <h3 className="font-bold">Address Balance:</h3>
                  <pre className="p-2 bg-gray-100 rounded text-sm">
                    Current: {addressResult.stats.currentBalance} BTC<br />
                    Total Received: {addressResult.stats.totalReceived} BTC<br />
                    Total Sent: {addressResult.stats.totalSent} BTC
                  </pre>
                  
                  <h3 className="font-bold mt-4">Recent Transactions:</h3>
                  <div className="rounded overflow-hidden border">
                    <table className="min-w-full">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-4 py-2 text-left">Transaction ID</th>
                          <th className="px-4 py-2 text-left">Type</th>
                          <th className="px-4 py-2 text-left">Amount</th>
                          <th className="px-4 py-2 text-left">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {addressResult.transactions.map((tx: any) => (
                          <tr key={tx.txid} className="border-t">
                            <td className="px-4 py-2 font-mono text-xs">
                              {tx.txid.substring(0, 10)}...
                            </td>
                            <td className="px-4 py-2 capitalize">
                              {tx.type}
                            </td>
                            <td className="px-4 py-2">
                              {tx.amount} BTC
                            </td>
                            <td className="px-4 py-2">
                              {tx.timestamp ? new Date(tx.timestamp * 1000).toLocaleDateString() : 'Pending'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="transaction">
          <Card>
            <CardHeader>
              <CardTitle>Transaction Data Test</CardTitle>
              <CardDescription>
                Enter a Bitcoin transaction ID to retrieve its details.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex space-x-2">
                <Input
                  value={txidInput}
                  onChange={(e) => setTxidInput(e.target.value)}
                  placeholder="Transaction ID (txid)"
                  className="flex-grow"
                />
                <Button 
                  onClick={() => txMutation.mutate()}
                  disabled={txMutation.isPending}
                >
                  {txMutation.isPending ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading</>
                  ) : (
                    'Fetch Tx'
                  )}
                </Button>
              </div>

              {txResult && (
                <div className="mt-4 max-h-[500px] overflow-auto">
                  <h3 className="font-bold">Transaction Details:</h3>
                  <pre className="p-2 bg-gray-100 rounded text-sm">
                    TXID: {txResult.txid}<br />
                    Status: {txResult.status.confirmed ? 'Confirmed' : 'Pending'}<br />
                    {txResult.status.confirmed && (
                      <>
                        Block: {txResult.status.block_height}<br />
                        Block Time: {new Date(txResult.status.block_time! * 1000).toLocaleString()}<br />
                      </>
                    )}
                    Fee: {txResult.fee} BTC<br />
                    Size: {txResult.size} bytes
                  </pre>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div>
                      <h3 className="font-bold">Inputs ({txResult.vin.length}):</h3>
                      <div className="rounded overflow-hidden border max-h-[300px] overflow-auto">
                        <table className="min-w-full">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="px-4 py-2 text-left">From</th>
                              <th className="px-4 py-2 text-left">Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {txResult.vin.map((input, index) => (
                              <tr key={index} className="border-t">
                                <td className="px-4 py-2 font-mono text-xs">
                                  {input.prevout?.scriptpubkey_address || 'Unknown'}
                                </td>
                                <td className="px-4 py-2">
                                  {input.prevout?.value || 0} BTC
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="font-bold">Outputs ({txResult.vout.length}):</h3>
                      <div className="rounded overflow-hidden border max-h-[300px] overflow-auto">
                        <table className="min-w-full">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="px-4 py-2 text-left">To</th>
                              <th className="px-4 py-2 text-left">Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {txResult.vout.map((output, index) => (
                              <tr key={index} className="border-t">
                                <td className="px-4 py-2 font-mono text-xs">
                                  {output.scriptpubkey_address || 'Unknown'}
                                </td>
                                <td className="px-4 py-2">
                                  {output.value} BTC
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="search">
          <Card>
            <CardHeader>
              <CardTitle>Blockchain Search Test</CardTitle>
              <CardDescription>
                Search for an address, transaction, or block by entering a query.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex space-x-2">
                <Input
                  value={queryInput}
                  onChange={(e) => setQueryInput(e.target.value)}
                  placeholder="Address, transaction ID, or block hash/height"
                  className="flex-grow"
                />
                <Button 
                  onClick={() => searchMutation.mutate()}
                  disabled={searchMutation.isPending}
                >
                  {searchMutation.isPending ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading</>
                  ) : (
                    'Search'
                  )}
                </Button>
              </div>

              {searchResult && (
                <div className="mt-4">
                  <h3 className="font-bold">Search Results:</h3>
                  <pre className="p-2 bg-gray-100 rounded text-sm">
                    Type: {searchResult.type}<br />
                    {searchResult.type !== 'unknown' && (
                      <div>
                        <p>Found matching {searchResult.type}:</p>
                        <div className="max-h-[400px] overflow-auto mt-2">
                          {JSON.stringify(searchResult.data, null, 2)}
                        </div>
                      </div>
                    )}
                    {searchResult.type === 'unknown' && (
                      <p>No results found for this query.</p>
                    )}
                  </pre>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}